import { debugLog } from './engineDebug.js';

/**
 * CardTracker - Tracks cards seen during a Ronda round for HARD mode AI decisions.
 *
 * In Ronda, cards are not reshuffled between hands within a round.
 * This tracker remembers all cards that have been played/seen to enable
 * intelligent decision-making (caída risk, aseo avoidance, etc.)
 */
class CardTracker {
    constructor() {
        this.reset();
    }

    /**
     * Reset all tracking data. Call at the start of each new round (when deck is shuffled).
     */
    reset() {
        this.seenCards = new Set(); // Set of "suit:value" strings
        this.centerCards = []; // Current cards in the center
        this.collectedByTeam = { 1: [], 2: [] };
    }

    /**
     * Record a single card as seen (played or revealed).
     * @param {Object} card - { suit, value }
     */
    recordCard(card) {
        if (card) {
            this.seenCards.add(`${card.suit}:${card.value}`);
        }
    }

    /**
     * Record multiple cards as seen.
     * @param {Array} cards
     */
    recordMultiple(cards) {
        if (cards) {
            cards.forEach(c => this.recordCard(c));
        }
    }

    /**
     * Set the current center cards (also marks them as seen).
     * @param {Array} cards
     */
    setCenterCards(cards) {
        this.centerCards = cards ? [...cards] : [];
        this.recordMultiple(cards);
    }

    /**
     * Record cards collected by a team.
     * @param {Array} cards
     * @param {number} teamId - 1 or 2
     */
    recordCollection(cards, teamId) {
        if (cards && this.collectedByTeam[teamId]) {
            this.collectedByTeam[teamId].push(...cards);
            this.recordMultiple(cards);
        }
    }

    /**
     * Get all unseen cards (cards not yet played/revealed).
     * Based on the full 40-card Spanish deck.
     * @returns {Array} Array of { suit, value } for unseen cards
     */
    getUnseenCards() {
        const suits = ['oros', 'copas', 'espadas', 'bastos'];
        const values = [1, 2, 3, 4, 5, 6, 7, 10, 11, 12];
        const unseen = [];

        for (const suit of suits) {
            for (const value of values) {
                if (!this.seenCards.has(`${suit}:${value}`)) {
                    unseen.push({ suit, value });
                }
            }
        }
        return unseen;
    }

    /**
     * Get how many unseen cards remain for a specific value.
     * Max is 4 (one per suit). Useful for estimating risk.
     * @param {number} value
     * @returns {number} Count of unseen cards with that value (0-4)
     */
    getRemainingCountByValue(value) {
        return this.getUnseenCards().filter(c => c.value === value).length;
    }

    /**
     * Estimate caída risk if we drop a card of this value to the center.
     * Caída happens when the next player has a card of the same value.
     *
     * Rough probability: remainingCount / maxPossible (3, since we hold 1).
     * @param {number} value - Card value to evaluate
     * @returns {number} Risk score between 0.0 (no risk) and 1.0 (high risk)
     */
    getCaidaRisk(value) {
        const remaining = this.getRemainingCountByValue(value);
        // We hold 1 card of this value, so max 3 others could exist
        // But some may have been seen already. The remaining count already factors that in.
        return Math.min(remaining / 3, 1.0);
    }

    /**
     * Check if dropping a card to the center would leave an aseo opportunity.
     * An aseo (clearing all center cards) is easier when all center cards share one value.
     * @param {Object} card - The card we're considering dropping
     * @param {Array} currentCenterCards - Current cards in the center
     * @returns {boolean} True if dropping this card would create an easy aseo for the next player
     */
    wouldLeaveAseo(card, currentCenterCards) {
        if (!currentCenterCards || currentCenterCards.length === 0) {
            // Dropping to an empty center means only our card is there -
            // any matching card from the next player = aseo
            return true;
        }

        const hypotheticalCenter = [...currentCenterCards, card];
        // If all cards in center share one value, it's trivially easy to aseo
        const uniqueValues = new Set(hypotheticalCenter.map(c => c.value));
        if (uniqueValues.size === 1) return true;

        // Also check if just 2 unique values - still relatively easy
        // But for now, only flag the most obvious case
        return false;
    }

    /**
     * Get the total number of cards that have been seen.
     * @returns {number}
     */
    getSeenCount() {
        return this.seenCards.size;
    }

    /**
     * Check if a specific card has been seen.
     * @param {Object} card - { suit, value }
     * @returns {boolean}
     */
    hasBeenSeen(card) {
        return this.seenCards.has(`${card.suit}:${card.value}`);
    }
}

export default CardTracker;
